// --------------------------------------------------------
// VITAL SIGN REGISTER - VitalSignRegister
// --------------------------------------------------------

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  Text,
} from "../../components/UI/generalLayout/Layout.styled";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import LabelInput from "../../components/labelInput/LabelInput";
import { useAppDispatch } from "../..";
import * as vitalSignActions from "../../store/actions/vitalSign";
import VitalSign from "../../models/core/careLine/VitalSign";
import { Paths } from "../../enums/Paths";
import Colors from "../../constants/Colors";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  TextField
} from "@mui/material";
import SuccessModal from "../../components/successModal/SuccessModal";
import { ReactComponent as CareLineModal } from "../../assets/imgs/careLineModal.svg";

import VitalSignInstrument from "../../models/core/careLine/VitalSignInstrument";
import { BiPlus, BiTrash } from "react-icons/bi";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Radio, CircularProgress } from "@mui/material";

function VitalSignRegister(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const [vitalSign, setVitalSign] = useState<VitalSign>(new VitalSign());

  useEffect(() => {
    const loadVitalSign = async (id: string) => {
      setLoading(true);
      try {
        const resp: any = await dispatch(vitalSignActions.fetchVitalSignById(id));
        if (resp && resp.data) {
          const vs = new VitalSign().fromJSON(resp.data);
          setVitalSign(vs);
        }
      } catch (error) {
        console.error("Error loading vital sign:", error);
      } finally {
        setLoading(false);
      }
    };

    const state = location.state as any;
    if (state && state.vitalSign) {
      setIsEdit(true);
      if (state.isView) {
        setIsView(true);
      }
      
      if (state.vitalSign.id) {
        loadVitalSign(state.vitalSign.id);
      } else {
        const vs = new VitalSign().fromJSON(state.vitalSign);
        setVitalSign(vs);
      }
    }
  }, [location.state, dispatch]);

  const handleSave = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const dataToSave = new VitalSign().fromJSON({
        ...vitalSign,
        tipoCampo: vitalSign.tipoCampo || "numerico"
      });

      if (isEdit) {
        await dispatch(vitalSignActions.updateVitalSign(dataToSave, dataToSave.id!));
      } else {
        await dispatch(vitalSignActions.registerVitalSign(dataToSave));
      }
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error saving vital sign:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    const updated = new VitalSign().fromJSON({ ...vitalSign, [field]: value });
    setVitalSign(updated);
  };

  const handleAddInstrument = () => {
    const newInst = new VitalSignInstrument();
    newInst.nome = "";
    newInst.padrao = vitalSign.instrumentos.length === 0;
    
    handleChange("instrumentos", [...vitalSign.instrumentos, newInst]);
  };

  const handleRemoveInstrument = (index: number) => {
    const newList = [...vitalSign.instrumentos];
    newList.splice(index, 1);
    
    // If we removed the default, set the first one as default
    if (newList.length > 0 && !newList.some(i => i.padrao)) {
      newList[0].padrao = true;
    }
    
    handleChange("instrumentos", newList);
  };

  const handleInstrumentChange = (index: number, field: string, value: any) => {
    const newList = [...vitalSign.instrumentos];
    
    const updatedItem = new VitalSignInstrument().fromJSON({ ...newList[index], [field]: value });
    newList[index] = updatedItem;
    
    if (field === "padrao" && value === true) {
      // Uncheck others
      newList.forEach((item, idx) => {
        if (idx !== index && item.padrao) {
          newList[idx] = new VitalSignInstrument().fromJSON({ ...item, padrao: false });
        }
      });
    }
    
    handleChange("instrumentos", newList);
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <Text
          style={{ cursor: "pointer", color: Colors.SPM600, marginBottom: 10 }}
          onClick={() => navigate(Paths.VitalSignList)}
        >
          {"< Voltar"}
        </Text>
        <ScreenHeader
          screenTitle={isView ? "Visualizar Sinal Vital" : isEdit ? "Editar Sinal Vital" : "Cadastrar Sinal Vital"}
          rightButtonText={!isView ? (loading ? "Salvando..." : "Salvar") : ""}
          hasRightButton={!isView}
          onRightButtonClick={handleSave}
          rightButtonIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          loading={loading}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 20, maxWidth: 800 }}>
          <LabelInput
            label="Nome do Sinal Vital"
            value={vitalSign.nome || ""}
            disabled={isView}
            onChangeText={(e: any) => handleChange("nome", e.target.value)}
          />

          <div style={{ display: "flex", gap: 20 }}>
            <FormControl fullWidth disabled={isView}>
              <InputLabel>Tipo de Campo</InputLabel>
              <Select
                value={vitalSign.tipoCampo || "numerico"}
                label="Tipo de Campo"
                onChange={(e) => handleChange("tipoCampo", e.target.value)}
              >
                <MenuItem value="numerico">Numérico</MenuItem>
                <MenuItem value="texto">Texto</MenuItem>
                <MenuItem value="calculado">Calculado (Fórmula)</MenuItem>
              </Select>
            </FormControl>

            <LabelInput
              label="Ordem de Exibição"
              type="number"
              value={vitalSign.ordem || 0}
              disabled={isView}
              onChangeText={(e: any) => handleChange("ordem", parseInt(e.target.value))}
            />
          </div>

          <div style={{ display: "flex", gap: 20 }}>
            <LabelInput
              label="Unidade Padrão"
              value={vitalSign.unidadePadrao || ""}
              disabled={isView}
              onChangeText={(e: any) => handleChange("unidadePadrao", e.target.value)}
            />
          </div>

          <LabelInput
            label="Opções de Unidades (separadas por vírgula)"
            value={vitalSign.unidades || ""}
            disabled={isView}
            onChangeText={(e: any) => handleChange("unidades", e.target.value)}
            placeholder="Ex: GRAU, RPM, BPM, MMHG"
          />

          <LabelInput
            label="Opções de Locais (separadas por vírgula)"
            value={vitalSign.locais || ""}
            disabled={isView}
            onChangeText={(e: any) => handleChange("locais", e.target.value)}
            placeholder="Ex: TODO CORPO, ABDOMEN DIREITO, ABDOMEN ESQUERDO, BRAÇO DIREITO, BRAÇO ESQUERDO"
          />

          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <Text bold fontSize="16px" color={Colors.SPM700}>Instrumentos</Text>
              {!isView && (
                <Button variant="outlined" startIcon={<BiPlus />} onClick={handleAddInstrument}>
                  Adicionar Instrumento
                </Button>
              )}
            </div>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome do Instrumento</TableCell>
                    <TableCell align="center" style={{ width: 100 }}>Padrão</TableCell>
                    {!isView && <TableCell align="center" style={{ width: 80 }}>Ações</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vitalSign.instrumentos.map((inst, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={inst.nome || ""}
                          disabled={isView}
                          onChange={(e) => handleInstrumentChange(index, "nome", e.target.value)}
                          placeholder="Ex: Termômetro Digital"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={inst.padrao}
                          disabled={isView}
                          onChange={() => handleInstrumentChange(index, "padrao", true)}
                        />
                      </TableCell>
                      {!isView && (
                        <TableCell align="center">
                          <IconButton color="error" onClick={() => handleRemoveInstrument(index)}>
                            <BiTrash />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {vitalSign.instrumentos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Text color="#667085">Nenhum instrumento cadastrado.</Text>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <LabelInput
            label="Máscara (ex: 000/000 ou cm:999;m:9.99)"
            value={vitalSign.mascara || ""}
            disabled={isView}
            onChangeText={(e: any) => handleChange("mascara", e.target.value)}
            placeholder="Use 'unidade:mascara;' para múltiplas unidades"
          />

          {vitalSign.tipoCampo === "calculado" && (
            <LabelInput
              label="Fórmula de Cálculo"
              value={vitalSign.formula || ""}
              disabled={isView}
              onChangeText={(e: any) => handleChange("formula", e.target.value)}
              placeholder="Ex: [PESO] / ([ALTURA] * [ALTURA])"
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={vitalSign.padrao}
                disabled={isView}
                onChange={(e) => handleChange("padrao", e.target.checked)}
              />
            }
            label="Sinal Vital Padrão (Exibir automaticamente na triagem)"
          />
        </div>

        <SuccessModal
          visible={showSuccessModal}
          onVoltar={() => {
            setShowSuccessModal(false);
            navigate(Paths.VitalSignList);
          }}
          Icon={CareLineModal}
          modalMsg={`Sinal vital ${isEdit ? "atualizado" : "cadastrado"} com sucesso!`}
        />
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default VitalSignRegister;
